
import React, { useState, useEffect, useMemo } from 'react';
import './ProjectTemplate.css'; // Your professional CSS file
import {
    IoAddOutline,
    IoArrowBack,
    IoFileTrayStackedOutline,
    IoGrid,
    IoList,
    IoSearchOutline,
    IoAddCircleOutline,
    IoRemoveCircleOutline,
    IoPencil,
} from 'react-icons/io5';

// --- API Configuration ---
const API_BASE_URL = process.env.REACT_APP_SWAGGER_SERVICE_BASE_URL;
const GET_PROJECT_TEMPLATES_ENDPOINT = `${API_BASE_URL}/api/get-Project Template`;
const CREATE_PROJECT_TEMPLATE_ENDPOINT = `${API_BASE_URL}/api/create-project-template`;
const UPDATE_PROJECT_TEMPLATE_ENDPOINT = `${API_BASE_URL}/api/update-project-template`;
const AUTH_TOKEN_KEY = 'zTaskAuthToken';
const USER_DETAILS_KEY = 'zTaskUserDetails';


// --- Child Components for Different Views ---

const Loader = () => <div className="pt-loader"></div>;

const TemplateCard = ({ template, onSelect, onEdit }) => (
    <div className="pt-card" onClick={() => onSelect(template)}>
        <div className="pt-card-content">
            <div className="pt-card-main-info">
                <div className="pt-card-icon-header"><IoFileTrayStackedOutline /></div>
                <h3 className="pt-card-title">{template.projectTemplateName}</h3>
                <p className="pt-card-subtitle">{template.mainTasks.length > 0 ? template.mainTasks[0].mainTaskTitle : 'No main tasks'}</p>
            </div>
            <div className="pt-card-footer">
                {/* --- UPDATED PART --- */}
                <span className="pt-subtask-count-badge">
                    {template.mainTasks.length} Main Tasks
                </span>
                <span className="pt-subtask-count-badge">
                    {template.mainTasks.reduce((acc, task) => acc + task.subTasks.length, 0)} Sub Tasks
                </span>
                {/* --- END OF UPDATE --- */}
                <button
                    className="pt-edit-btn"
                    onClick={(e) => {
                        e.stopPropagation();
                        onEdit(template);
                    }}
                    title="Edit Template"
                >
                    <IoPencil />
                </button>
            </div>
        </div>
    </div>
);


const TemplateListView = ({ templates, setView, onSelectTemplate, onEditTemplate, canCreateTemplate }) => {
    const [viewMode, setViewMode] = useState('GRID'); // GRID or LIST
    const [searchTerm, setSearchTerm] = useState('');

    const filteredTemplates = useMemo(() =>
        templates.filter(template =>
            template.projectTemplateName.toLowerCase().includes(searchTerm.toLowerCase())
        ), [templates, searchTerm]);

    return (
        <div className="pt-page">
            <div className="pt-list-header">
                <div className="pt-header-text">
                    <h1>Project Templates</h1>
                    <p>Manage and create reusable templates for your projects.</p>
                </div>
                <div className="pt-header-actions">
                    <div className="pt-search-container">
                        <input
                            type="text"
                            className="pt-search-input"
                            placeholder="Search templates..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <IoSearchOutline className="pt-search-icon" />
                    </div>
                    <div className="pt-view-toggle">
                        <button className={`pt-view-btn ${viewMode === 'GRID' ? 'active' : ''}`} onClick={() => setViewMode('GRID')} title="Grid View"><IoGrid /></button>
                        <button className={`pt-view-btn ${viewMode === 'LIST' ? 'active' : ''}`} onClick={() => setViewMode('LIST')} title="List View"><IoList /></button>
                    </div>
                    {canCreateTemplate && (
                         <button
                            className="pt-create-btn-header"
                            onClick={() => setView('CREATE')}
                            title={'Create New Template'}
                        >
                            <IoAddOutline className="pt-icon" /> Create New
                        </button>
                    )}
                </div>
            </div>

            <div className="pt-content">
                {filteredTemplates.length > 0 ? (
                    <div className={`pt-grid ${viewMode === 'LIST' ? 'pt-list-view' : ''}`}>
                        {filteredTemplates.map(template => (
                            <TemplateCard
                                key={template.projectTemplateId}
                                template={template}
                                onSelect={onSelectTemplate}
                                onEdit={onEditTemplate}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="pt-no-results-message">
                        <h3>{searchTerm ? 'No templates found' : 'No Project Templates Yet'}</h3>
                        <p>{searchTerm ? 'Try adjusting your search term.' : 'Get started by creating a new template.'}</p>
                    </div>
                )}
            </div>
        </div>
    );
};

const TemplateCreateView = ({ onCancel, onSubmit }) => {
    const [templateName, setTemplateName] = useState('');
    const [mainTasks, setMainTasks] = useState([{ title: '', subTasks: [''] }]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // --- Main Task Handlers ---
    const handleAddMainTask = () => setMainTasks([...mainTasks, { title: '', subTasks: [''] }]);
    const handleRemoveMainTask = (index) => setMainTasks(mainTasks.filter((_, i) => i !== index));
    const handleMainTaskChange = (index, value) => {
        const newMainTasks = [...mainTasks];
        newMainTasks[index].title = value;
        setMainTasks(newMainTasks);
    };

    // --- Sub Task Handlers ---
    const handleAddSubTask = (mainTaskIndex) => {
        const newMainTasks = [...mainTasks];
        newMainTasks[mainTaskIndex].subTasks.push('');
        setMainTasks(newMainTasks);
    };
    const handleRemoveSubTask = (mainTaskIndex, subTaskIndex) => {
        const newMainTasks = [...mainTasks];
        newMainTasks[mainTaskIndex].subTasks = newMainTasks[mainTaskIndex].subTasks.filter((_, i) => i !== subTaskIndex);
        setMainTasks(newMainTasks);
    };
    const handleSubTaskChange = (mainTaskIndex, subTaskIndex, value) => {
        const newMainTasks = [...mainTasks];
        newMainTasks[mainTaskIndex].subTasks[subTaskIndex] = value;
        setMainTasks(newMainTasks);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        // Format data for the API
        const templateData = {
            projectTemplateName: templateName,
            mainTaskTitles: mainTasks.map(mt => mt.title).filter(t => t.trim() !== ''),
            subTasksByMainTask: mainTasks.reduce((acc, mt) => {
                if (mt.title.trim() !== '') {
                    acc[mt.title] = mt.subTasks.filter(st => st.trim() !== '');
                }
                return acc;
            }, {}),
        };

        await onSubmit(templateData);
        setIsSubmitting(false);
    };

    return (
        <div className="pt-editor-page">
            <div className="pt-editor-header">
                <button onClick={onCancel} className="pt-back-button" title="Cancel"><IoArrowBack /></button>
                <h2>Create New Project Template</h2>
            </div>
            <div className="pt-editor-content">
                <form onSubmit={handleSubmit} className="pt-form-content">
                    <div className="pt-form-group">
                        <label htmlFor="templateName">Template Name <span className="pt-required-mark">*</span></label>
                        <input type="text" id="templateName" value={templateName} onChange={(e) => setTemplateName(e.target.value)} required />
                    </div>

                    <hr className="pt-divider" />

                    {mainTasks.map((mainTask, mainIndex) => (
                        <div key={mainIndex} className="pt-main-task-block">
                            <div className="pt-form-group">
                                <label htmlFor={`mainTaskTitle-${mainIndex}`}>Main Task Title #{mainIndex + 1} <span className="pt-required-mark">*</span></label>
                                <div className="pt-main-task-input-wrapper">
                                    <input type="text" id={`mainTaskTitle-${mainIndex}`} value={mainTask.title} onChange={(e) => handleMainTaskChange(mainIndex, e.target.value)} required />
                                    {mainTasks.length > 1 && <IoRemoveCircleOutline className="pt-action-icon pt-remove-icon" onClick={() => handleRemoveMainTask(mainIndex)} title="Remove Main Task"/>}
                                </div>
                            </div>
                            <div className="pt-form-group pt-subtask-group">
                                <label>Sub Tasks</label>
                                {mainTask.subTasks.map((subTask, subIndex) => (
                                    <div key={subIndex} className="pt-sub-task-item">
                                        <input type="text" value={subTask} onChange={(e) => handleSubTaskChange(mainIndex, subIndex, e.target.value)} required placeholder={`Sub Task ${subIndex + 1}`} />
                                        {mainTask.subTasks.length > 1 && <IoRemoveCircleOutline className="pt-action-icon pt-remove-icon" onClick={() => handleRemoveSubTask(mainIndex, subIndex)} />}
                                    </div>
                                ))}
                                <div className="pt-add-subtask-container">
                                    <IoAddCircleOutline className="pt-action-icon pt-add-icon" onClick={() => handleAddSubTask(mainIndex)} />
                                    <span onClick={() => handleAddSubTask(mainIndex)} className="pt-add-subtask-text">Add Sub Task</span>
                                </div>
                            </div>
                        </div>
                    ))}

                    <button type="button" className="pt-button-add-main" onClick={handleAddMainTask}>
                        <IoAddOutline /> Add Another Main Task
                    </button>
                </form>
                <div className="pt-editor-actions">
                    <button type="button" className="pt-button-cancel" onClick={onCancel}>Cancel</button>
                    <button type="submit" className="pt-button-submit" onClick={handleSubmit} disabled={isSubmitting}>
                        {isSubmitting ? 'Saving...' : 'Save Template'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const TemplateEditView = ({ template, onCancel, onSubmit }) => {
    const [templateName, setTemplateName] = useState(template.projectTemplateName);
    const [mainTasks, setMainTasks] = useState(template.mainTasks.map(mt => ({
        title: mt.mainTaskTitle,
        subTasks: mt.subTasks.map(st => st.subTaskTitle)
    })));
    const [isSubmitting, setIsSubmitting] = useState(false);


    const handleAddMainTask = () => setMainTasks([...mainTasks, { title: '', subTasks: [''] }]);
    const handleRemoveMainTask = (index) => setMainTasks(mainTasks.filter((_, i) => i !== index));
    const handleMainTaskChange = (index, value) => {
        const newMainTasks = [...mainTasks];
        newMainTasks[index].title = value;
        setMainTasks(newMainTasks);
    };


    const handleAddSubTask = (mainTaskIndex) => {
        const newMainTasks = [...mainTasks];
        newMainTasks[mainTaskIndex].subTasks.push('');
        setMainTasks(newMainTasks);
    };
    const handleRemoveSubTask = (mainTaskIndex, subTaskIndex) => {
        const newMainTasks = [...mainTasks];
        newMainTasks[mainTaskIndex].subTasks = newMainTasks[mainTaskIndex].subTasks.filter((_, i) => i !== subTaskIndex);
        setMainTasks(newMainTasks);
    };
    const handleSubTaskChange = (mainTaskIndex, subTaskIndex, value) => {
        const newMainTasks = [...mainTasks];
        newMainTasks[mainTaskIndex].subTasks[subTaskIndex] = value;
        setMainTasks(newMainTasks);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        const templateData = {
            projectTemplateName: templateName,
            mainTaskTitles: mainTasks.map(mt => mt.title).filter(t => t.trim() !== ''),
            subTasksByMainTask: mainTasks.reduce((acc, mt) => {
                if (mt.title.trim() !== '') {
                    acc[mt.title] = mt.subTasks.filter(st => st.trim() !== '');
                }
                return acc;
            }, {}),
        };

        await onSubmit(template.projectTemplateId, templateData);
        setIsSubmitting(false);
    };

    return (
        <div className="pt-editor-page">
            <div className="pt-editor-header">
                <button onClick={onCancel} className="pt-back-button" title="Cancel"><IoArrowBack /></button>
                <h2>Edit Project Template</h2>
            </div>
            <div className="pt-editor-content">
                <form onSubmit={handleSubmit} className="pt-form-content">
                    <div className="pt-form-group">
                        <label htmlFor="templateName">Template Name <span className="pt-required-mark">*</span></label>
                        <input type="text" id="templateName" value={templateName} onChange={(e) => setTemplateName(e.target.value)} required />
                    </div>

                    <hr className="pt-divider" />

                    {mainTasks.map((mainTask, mainIndex) => (
                        <div key={mainIndex} className="pt-main-task-block">
                            <div className="pt-form-group">
                                <label htmlFor={`mainTaskTitle-${mainIndex}`}>Main Task Title #{mainIndex + 1} <span className="pt-required-mark">*</span></label>
                                <div className="pt-main-task-input-wrapper">
                                    <input type="text" id={`mainTaskTitle-${mainIndex}`} value={mainTask.title} onChange={(e) => handleMainTaskChange(mainIndex, e.target.value)} required />
                                    {mainTasks.length > 1 && <IoRemoveCircleOutline className="pt-action-icon pt-remove-icon" onClick={() => handleRemoveMainTask(mainIndex)} title="Remove Main Task" />}
                                </div>
                            </div>
                            <div className="pt-form-group pt-subtask-group">
                                <label>Sub Tasks</label>
                                {mainTask.subTasks.map((subTask, subIndex) => (
                                    <div key={subIndex} className="pt-sub-task-item">
                                        <input type="text" value={subTask} onChange={(e) => handleSubTaskChange(mainIndex, subIndex, e.target.value)} required placeholder={`Sub Task ${subIndex + 1}`} />
                                        {mainTask.subTasks.length > 1 && <IoRemoveCircleOutline className="pt-action-icon pt-remove-icon" onClick={() => handleRemoveSubTask(mainIndex, subIndex)} />}
                                    </div>
                                ))}
                                <div className="pt-add-subtask-container">
                                    <IoAddCircleOutline className="pt-action-icon pt-add-icon" onClick={() => handleAddSubTask(mainIndex)} />
                                    <span onClick={() => handleAddSubTask(mainIndex)} className="pt-add-subtask-text">Add Sub Task</span>
                                </div>
                            </div>
                        </div>
                    ))}

                    <button type="button" className="pt-button-add-main" onClick={handleAddMainTask}>
                        <IoAddOutline /> Add Another Main Task
                    </button>
                </form>
                <div className="pt-editor-actions">
                    <button type="button" className="pt-button-cancel" onClick={onCancel}>Cancel</button>
                    <button type="submit" className="pt-button-submit" onClick={handleSubmit} disabled={isSubmitting}>
                        {isSubmitting ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const TemplateDetailsView = ({ template, onBack }) => (
    <div className="pt-editor-page">
        <div className="pt-editor-header">
            <button onClick={onBack} className="pt-back-button" title="Back to list"><IoArrowBack /></button>
            <h2>{template.projectTemplateName}</h2>
        </div>
        <div className="pt-editor-content">
            <div className="pt-details-container">
                {template.mainTasks && template.mainTasks.map((mainTask, index) => (
                    <div className="pt-detail-item full-width" key={index}>
                        <span className="pt-detail-label">MAIN TASK: {mainTask.mainTaskTitle}</span>
                        {mainTask.subTasks && mainTask.subTasks.length > 0 ? (
                            <ul className="pt-subtasks-list">
                                {mainTask.subTasks.map((subTask, subIndex) => <li key={subIndex}>{subTask.subTaskTitle}</li>)}
                            </ul>
                        ) : (
                            <p className="pt-no-items-message">No sub-tasks for this main task.</p>
                        )}
                    </div>
                ))}
                 {(!template.mainTasks || template.mainTasks.length === 0) && (
                    <p className="pt-no-items-message">No main tasks defined for this template.</p>
                )}
            </div>
        </div>
    </div>
);


// --- Main Component ---
const ProjectTemplate = () => {
    const [view, setView] = useState('LIST');
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [templates, setTemplates] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // --- PERMISSION CHECK ---
    // Check for 'CREATE PROJECT TEMPLATE' permission from local storage.
    const canCreateTemplate = useMemo(() => {
        const userDetailsString = localStorage.getItem(USER_DETAILS_KEY);
        if (!userDetailsString) {
            return false;
        }
        try {
            const userDetails = JSON.parse(userDetailsString);
            const permissions = userDetails?.role?.permissions || [];
            return permissions.some(permission => permission.title === 'CREATE PROJECT TEMPLATE');
        } catch (error) {
            console.error("Failed to parse user details for permission check:", error);
            return false;
        }
    }, []);


    const fetchTemplates = async () => {
        setIsLoading(true);
        setError(null);
        const token = localStorage.getItem(AUTH_TOKEN_KEY);
        try {
            const response = await fetch(GET_PROJECT_TEMPLATES_ENDPOINT, { headers: { 'Authorization': `Bearer ${token}` } });
            if (response.ok) {
                const data = await response.json();
                setTemplates(data.project_template || []);
            } else if (response.status === 404) {
                setTemplates([]);
            } else {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to fetch project templates.');
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => { fetchTemplates(); }, []);

    const handleCreateTemplate = async (templateData) => {
        const token = localStorage.getItem(AUTH_TOKEN_KEY);
        try {
            const response = await fetch(CREATE_PROJECT_TEMPLATE_ENDPOINT, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    'accept': 'application/json'
                },
                body: JSON.stringify(templateData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to create template.');
            }

            // On success
            await fetchTemplates(); // Refresh the list
            setView('LIST'); // Go back to the list view
        } catch (err) {
            alert(`Error: ${err.message}`);
        }
    };

    const handleUpdateTemplate = async (templateId, templateData) => {
        const token = localStorage.getItem(AUTH_TOKEN_KEY);
        try {
            const response = await fetch(`${UPDATE_PROJECT_TEMPLATE_ENDPOINT}/${templateId}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    'accept': 'application/json'
                },
                body: JSON.stringify(templateData)
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to update template.');
            }
            await fetchTemplates();
            setView('LIST');
        } catch (err) {
            alert(`Error: ${err.message}`);
        }
    };

    const handleViewDetails = (template) => {
        setSelectedTemplate(template);
        setView('DETAILS');
    };

    const handleEditTemplate = (template) => {
        setSelectedTemplate(template);
        setView('EDIT');
    };


    if (isLoading) return <div className="pt-page"><Loader /></div>;
    if (error) return <div className="pt-page"><div className="pt-error-message">{error}</div></div>;

    switch (view) {
        case 'CREATE':
            return <TemplateCreateView onSubmit={handleCreateTemplate} onCancel={() => setView('LIST')} />;
        case 'EDIT':
            return <TemplateEditView template={selectedTemplate} onSubmit={handleUpdateTemplate} onCancel={() => setView('LIST')} />;
        case 'DETAILS':
            return <TemplateDetailsView template={selectedTemplate} onBack={() => setView('LIST')} />;
        default:
            return <TemplateListView
                        templates={templates}
                        setView={setView}
                        onSelectTemplate={handleViewDetails}
                        onEditTemplate={handleEditTemplate}
                        canCreateTemplate={canCreateTemplate}
                    />;
    }
};

export default ProjectTemplate;